
import * as React from 'react';

const digitsIn = (value: string) => value.replace(/\D/g, '');

export function digitsBefore(value: string, caret: number): number {
  return digitsIn(value.slice(0, caret)).length;
}

export function caretAfterDigits(value: string, count: number): number {
  if (count <= 0) return 0;
  let seen = 0;
  for (let at = 0; at < value.length; at += 1) {
    if (!/\d/.test(value[at])) continue;
    seen += 1;
    if (seen === count) return at + 1;
  }
  return value.length;
}

export function injectedDigits(raw: string, masked: string): number {
  const from = digitsIn(raw);
  const to = digitsIn(masked);
  for (let cut = 0; cut <= to.length; cut += 1) {
    if (from.startsWith(to.slice(cut))) return cut;
  }
  return 0;
}

export function caretAfterMask(raw: string, rawCaret: number, masked: string): number {
  const target = digitsBefore(raw, rawCaret) + injectedDigits(raw, masked);
  let at = caretAfterDigits(masked, target);
  while (at < masked.length && !/\d/.test(masked[at])) at += 1;
  return at;
}

export interface MaskedInputOptions {
  value: string;
  mask: (next: string, previous: string) => string;
  onChange: (value: string) => void;
}

export function useMaskedInput({ value, mask, onChange }: MaskedInputOptions): {
  ref: React.RefObject<HTMLInputElement>;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
} {
  const node = React.useRef<HTMLInputElement>(null);
  const pending = React.useRef<number | null>(null);

  const placeCaret = React.useCallback(() => {
    const el = node.current;
    if (el == null || pending.current == null) return;
    const at = pending.current;
    pending.current = null;
    el.setSelectionRange(at, at);
  }, []);

  React.useLayoutEffect(placeCaret);

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const raw = event.target.value;
    const rawCaret = event.target.selectionStart ?? raw.length;
    const masked = mask(raw, value);
    pending.current = caretAfterMask(raw, rawCaret, masked);
    onChange(masked);
    if (masked === value) {
      queueMicrotask(placeCaret);
    }
  };

  return { ref: node, onChange: handleChange };
}
